/**
 * Efforts Dashboard API layer (plain functions).
 *
 * Ported from the source repo's `EffortsApi` class. Every method keeps its name,
 * arguments and return shape, so `hooks/useEffortsDashboard.js` calls it
 * unchanged through `import * as effortsApi from '../api/effortsApi.js'`.
 *
 * Differences from the source class, following `SfeDashboard/api/sfeApi.js`:
 *   - Requests go through the shared `apiClient` (utils/axiosConfig) at
 *     `getApiUrl('efforts-dashboard/...')`. The interceptor attaches the
 *     Databricks token because `isDatabricksEndpoint()` matches
 *     `efforts-dashboard`. The source's middleware proxy path
 *     (`/api/v1/proxy-sales/api/supervisor/{encoded}`), its own axios instance,
 *     the token providers and the temporary X-API-Key header are not used.
 *   - Every response goes through `unwrapEnvelope`: `success: false` throws an
 *     `EffortsApiError` with the allow-listed backend message (utils/apiErrors)
 *     instead of silently returning the fallback shape.
 *   - Every rejection is an `EffortsApiError` carrying an `apiError`
 *     descriptor, so `formatErrorMessage` / DashboardKit `ErrorState` can
 *     render it directly. Aborts are rethrown untouched.
 *   - The inline mock data (`USE_MOCKS`, already `false` in the source) is
 *     dropped.
 */
import apiClient from "../../../utils/axiosConfig";
import { API_ENDPOINTS, getApiUrl } from "../../../Configurations/config";
import {
  ERROR_KIND,
  GENERIC_MESSAGES,
  normalizeApiError,
  readAllowListedMessage,
  readReferenceId,
} from "../../../utils/apiErrors";

const EP = API_ENDPOINTS.EFFORTS;

// ─── errors ───────────────────────────────────────────────────────────────────

/** Message used when a failed envelope carries no usable text of its own. */
const FALLBACK_FAILURE_MESSAGE = GENERIC_MESSAGES[ERROR_KIND.UNKNOWN];

/** Trimmed non-empty string, or null. */
function readString(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Plain-ish object test: excludes null and arrays. */
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * A failed Efforts request, whether it arrived as an HTTP error or as a
 * `success: false` envelope on a 200. `message` is always display-safe;
 * `apiError` mirrors the descriptor the shared Axios pipeline attaches.
 */
export class EffortsApiError extends Error {
  constructor(message, { referenceId = null, kind = ERROR_KIND.UNKNOWN, status = null, cause } = {}) {
    const safeMessage = readString(message) ?? FALLBACK_FAILURE_MESSAGE;
    super(safeMessage);
    this.name = "EffortsApiError";
    this.referenceId =
      readString(referenceId) ??
      (typeof referenceId === "number" && Number.isFinite(referenceId) ? String(referenceId) : null);
    this.kind = kind;
    this.status = status;
    if (cause !== undefined) this.cause = cause;
    this.apiError = {
      kind,
      status,
      message: safeMessage,
      referenceId: this.referenceId,
      retryAfterMs: null,
      raw: cause === undefined ? this : cause,
    };
  }
}

/** True for a cancellation (AbortController or Axios CanceledError). */
function isAbortError(error) {
  if (!isRecord(error)) return false;
  return error.name === "AbortError" || error.name === "CanceledError" || error.code === "ERR_CANCELED";
}

/** The response body, accepting either an Axios response or the body itself. */
function readBody(response) {
  if (!isRecord(response)) return response;
  if ("success" in response) return response;
  if ("data" in response) return response.data;
  return response;
}

/**
 * Unwraps the `{ success, data, message }` envelope.
 * - `success === false` → throws `EffortsApiError` with the backend message.
 * - `success` present and not false → returns `data`.
 * - `success` absent → returns the body unchanged.
 *
 * @param {unknown} response an Axios response, or the body on its own
 * @returns {unknown}
 * @throws {EffortsApiError}
 */
export function unwrapEnvelope(response) {
  const body = readBody(response);
  if (!isRecord(body) || !("success" in body)) return body;

  if (body.success === false) {
    const asError = { response: { data: body } };
    throw new EffortsApiError(readAllowListedMessage(asError) ?? FALLBACK_FAILURE_MESSAGE, {
      referenceId: readReferenceId(asError),
    });
  }
  return body.data;
}

/** Normalizes any rejection into an `EffortsApiError` (aborts pass through). */
function asEffortsApiError(error) {
  if (isAbortError(error) || error instanceof EffortsApiError) return error;
  const descriptor = isRecord(error) && isRecord(error.apiError) ? error.apiError : normalizeApiError(error);
  return new EffortsApiError(descriptor.message ?? FALLBACK_FAILURE_MESSAGE, {
    referenceId: descriptor.referenceId ?? null,
    kind: descriptor.kind ?? ERROR_KIND.UNKNOWN,
    status: descriptor.status ?? null,
    cause: error,
  });
}

/** One GET (endpoint from API_ENDPOINTS.EFFORTS), unwrapped; `{}` when `data` is empty. */
async function envelopeGet(endpoint, params = {}, { signal } = {}) {
  try {
    const response = await apiClient.get(getApiUrl(endpoint), { params, signal });
    const data = unwrapEnvelope(response);
    return isRecord(data) ? data : {};
  } catch (error) {
    throw asEffortsApiError(error);
  }
}

// ─── params ───────────────────────────────────────────────────────────────────

/** Backend accepts at most this many values per list filter. */
const MAX_FILTER_VALUES = 50;

/**
 * Normalise a filter value (string | string[] | null) into a trimmed,
 * de-duplicated, non-empty array capped at MAX_FILTER_VALUES.
 * @param {string|string[]|null|undefined} value
 * @returns {string[]}
 */
function toValueList(value) {
  const arr = Array.isArray(value) ? value : value == null ? [] : [value];
  const cleaned = arr.map((v) => (v == null ? "" : String(v).trim())).filter(Boolean);
  return [...new Set(cleaned)].slice(0, MAX_FILTER_VALUES);
}

/** Set `key=v1,v2,...` when the list has entries; empty = no filter. */
function setListParam(params, key, value) {
  const list = toValueList(value);
  if (list.length) params[key] = list.join(",");
}

/** Set a scalar param when it is a non-empty string/number. */
function setScalarParam(params, key, value) {
  if (value == null) return;
  const str = String(value).trim();
  if (str) params[key] = str;
}

/**
 * The params shared by every efforts endpoint: division / state / territory /
 * role (lists), yearmonth, and optionally tenure_band. The 12-month trend
 * endpoints must omit tenure_band (`{ includeTenure: false }`).
 */
function effortsParams(filters = {}, { includeTenure = true } = {}) {
  const { division, state, territory, yearmonth, role, tenureBand } = filters || {};
  const params = {};
  setListParam(params, "division", division);
  setListParam(params, "state", state);
  setListParam(params, "territory", territory);
  setScalarParam(params, "yearmonth", yearmonth);
  setListParam(params, "role", role);
  if (includeTenure) setListParam(params, "tenure_band", tenureBand);
  return params;
}

/** Only the division/state/territory lists (manager + correlation endpoints). */
function geoParams(filters = {}) {
  const { division, state, territory } = filters || {};
  const params = {};
  setListParam(params, "division", division);
  setListParam(params, "state", state);
  setListParam(params, "territory", territory);
  return params;
}

const asArray = (v) => (Array.isArray(v) ? v : []);
const asMap = (m) => (m && typeof m === "object" && !Array.isArray(m) ? m : {});

// ─── row normalisation (unchanged from the source class) ──────────────────────

/**
 * Maps the band-card metric ids used on the frontend to the backend's
 * `metric` query values for the band-employees drill-down endpoint.
 */
const BAND_METRIC_PARAM = {
  workingDays: "working_days",
  drCallAverage: "dr_call_avg",
  coverage: "coverage",
  compliance: "compliance",
};

/** First non-null/non-empty value from the args (string/number). */
function firstDefined(...vals) {
  for (const v of vals) {
    if (v != null && !(typeof v === "string" && v.trim() === "")) return v;
  }
  return null;
}

/**
 * Years between `joining` (ISO date) and now, 1-decimal, or null when the date
 * is missing/unparseable. Client fallback when the backend sends no
 * `tenure_years`.
 */
function tenureYearsFromDate(joining) {
  if (!joining) return null;
  const t = Date.parse(joining);
  if (Number.isNaN(t)) return null;
  const years = (Date.now() - t) / (365.25 * 24 * 60 * 60 * 1000);
  return years >= 0 ? Math.round(years * 10) / 10 : null;
}

/**
 * Normalise a per-employee trend/row object to the camelCase shape the Efforts
 * tabs consume, reading the new backend field names first and falling back to
 * the older ones:
 *   joining_date | joiningDate | doj          -> joiningDate
 *   tenure_years | tenureYears | (derived)    -> tenureYears (1 decimal)
 *   mis_hq | hq_name | misHq | hq             -> misHq
 *   employee_code | employeeCode | code | employeeId -> employeeCode
 */
function normEmployeeTrendRow(e) {
  if (!e || typeof e !== "object") return e;
  const joiningDate = firstDefined(e.joining_date, e.joiningDate, e.doj);
  const tenureYears = firstDefined(e.tenure_years, e.tenureYears, tenureYearsFromDate(joiningDate));
  const misHq = firstDefined(e.mis_hq, e.hq_name, e.misHq, e.hq);
  const employeeCode = firstDefined(e.employee_code, e.employeeCode, e.code, e.employeeId);
  return {
    ...e,
    employeeCode,
    name: firstDefined(e.name, e.employee_name) ?? null,
    joiningDate,
    tenureYears: tenureYears == null ? null : Number(tenureYears),
    misHq,
  };
}

// ─── endpoints ────────────────────────────────────────────────────────────────

/**
 * Available periods. GET efforts-dashboard/periods
 * @returns {Promise<{ fiscalYears: any[], latest: string|null }>}
 */
export async function getPeriods() {
  const res = await envelopeGet(EP.PERIODS);
  return {
    fiscalYears: asArray(res.fiscalYears),
    latest: res.latest ?? null,
  };
}

/**
 * Cascading filter options. GET efforts-dashboard/filter-options?yearmonth&division&state
 * @param {{ yearmonth?: string|null, division?: string[]|string, state?: string[]|string }} [params]
 */
export async function getFilterOptions({ yearmonth, division, state } = {}) {
  const params = {};
  setScalarParam(params, "yearmonth", yearmonth);
  setListParam(params, "division", division);
  setListParam(params, "state", state);
  const res = await envelopeGet(EP.FILTER_OPTIONS, params);
  return {
    yearmonth: res.yearmonth ?? (yearmonth ?? ""),
    divisions: asArray(res.divisions),
    states: asArray(res.states),
    hqs: asArray(res.hqs),
    // Per-option stats keyed by name (flsCount, avgWorkingDays, coveragePercent).
    divisionStats: asMap(res.divisionStats),
    stateStats: asMap(res.stateStats),
    hqStats: asMap(res.hqStats),
  };
}

/**
 * Paginated field-rep effort rows. GET efforts-dashboard/employees
 * @param {Object} [filters]
 * @param {number} [page=1]
 * @param {number} [pageSize=100] clamped to [1, 100]
 */
export async function getEffortsData(filters = {}, page = 1, pageSize = 100) {
  const safePageSize = Math.min(100, Math.max(1, Number(pageSize) || 100));
  const res = await envelopeGet(EP.EMPLOYEES, {
    page,
    page_size: safePageSize,
    ...effortsParams(filters),
  });
  return {
    rows: asArray(res.data),
    total: res.total || 0,
    page: res.page || page,
    page_size: res.page_size || safePageSize,
    total_pages: res.total_pages || 1,
  };
}

/** Precomputed KPI summary. GET efforts-dashboard/summary */
export async function getEffortsDataSummary(filters = {}) {
  const res = await envelopeGet(EP.SUMMARY, effortsParams(filters));
  return {
    kpis: asArray(res.kpis),
    employeeCount: res.employeeCount || 0,
  };
}

/** KPI band counts for the four top cards. GET efforts-dashboard/band-counts */
export async function getBandCounts(filters = {}) {
  const res = await envelopeGet(EP.BAND_COUNTS, effortsParams(filters));
  return {
    cards: asArray(res.cards),
    employeeCount: res.employeeCount || 0,
  };
}

/** Auto-generated insight lines. GET efforts-dashboard/insights */
export async function getInsights(filters = {}) {
  const res = await envelopeGet(EP.INSIGHTS, effortsParams(filters));
  return {
    insights: asArray(res.insights),
    employeeCount: res.employeeCount || 0,
    role: res.role ?? "",
    yearmonth: res.yearmonth ?? "",
  };
}

/**
 * Drill-down list for a preset insight. GET efforts-dashboard/insight-employees?insight=<preset>
 * @param {string} preset
 * @param {Object} [filters]
 */
export async function getInsightEmployees(preset, filters = {}) {
  const res = await envelopeGet(EP.INSIGHT_EMPLOYEES, { insight: preset, ...effortsParams(filters) });
  const employees = asArray(res.employees);
  return {
    insight: res.insight ?? preset,
    metricLabel: res.metricLabel ?? "Value",
    metricFormat: res.metricFormat ?? "number",
    total: res.total ?? employees.length,
    employees,
  };
}

/**
 * Monthly trend grouped by designation, Apr-Mar.
 * GET efforts-dashboard/designation-trend?group_by=role
 * @param {string} [fiscalYear] e.g. "2025-26"; omit for latest.
 * @param {Object} [filters]
 */
export async function getDesignationTrend(fiscalYear, filters = {}) {
  // The backend groups by ROLE; without group_by=role the table stays empty.
  const params = { group_by: "role" };
  setScalarParam(params, "fiscal_year", fiscalYear);
  // Trends span 12 months, so tenure_band is intentionally omitted.
  Object.assign(params, effortsParams(filters, { includeTenure: false }));
  const res = await envelopeGet(EP.DESIGNATION_TREND, params);
  return {
    fiscalYear: res.fiscalYear || "",
    months: asArray(res.months),
    series: asArray(res.series),
  };
}

/**
 * Per-employee monthly Dr Coverage % and Freq Compliance % trend, Apr-Mar.
 * GET efforts-dashboard/fls-trend
 */
export async function getFlsTrend(fiscalYear, filters = {}) {
  const params = {};
  setScalarParam(params, "fiscal_year", fiscalYear);
  Object.assign(params, effortsParams(filters, { includeTenure: false }));
  const res = await envelopeGet(EP.FLS_TREND, params);
  return {
    fiscalYear: res.fiscalYear || "",
    months: asArray(res.months),
    employees: asArray(res.employees).map(normEmployeeTrendRow),
  };
}

/**
 * Per-manager (FLM/SLM/TLM) monthly FWD + Dr. Call Avg, Apr-Mar.
 * GET efforts-dashboard/managers-employee-trend?tier&fiscal_year&division&state&territory
 * @param {string} [tier='FLM']
 */
export async function getManagersEmployeeTrend(tier = "FLM", fiscalYear, filters = {}) {
  const params = { tier };
  setScalarParam(params, "fiscal_year", fiscalYear);
  Object.assign(params, geoParams(filters));
  const res = await envelopeGet(EP.MANAGERS_EMPLOYEE_TREND, params);
  return {
    tier: res.tier ?? tier,
    fiscalYear: res.fiscalYear ?? "",
    months: asArray(res.months),
    employees: asArray(res.employees).map(normEmployeeTrendRow),
  };
}

/**
 * Monthly manager-tier trend, Apr-Mar.
 * GET efforts-dashboard/managers-trend?tier&fiscal_year&division&state&territory
 */
export async function getManagersTrend(tier = "FLM", fiscalYear, filters = {}) {
  const params = { tier };
  setScalarParam(params, "fiscal_year", fiscalYear);
  Object.assign(params, geoParams(filters));
  const res = await envelopeGet(EP.MANAGERS_TREND, params);
  return {
    tier: res.tier ?? tier,
    fiscalYear: res.fiscalYear ?? "",
    months: asArray(res.months),
    points: asArray(res.points),
  };
}

/**
 * Effort vs sales-performance correlation (2x2 quadrants).
 * GET efforts-dashboard/correlation?effort_axis&division&state&territory&yearmonth&tenure_band&sales_threshold&effort_threshold
 * @param {{ effortAxis?: string, salesThreshold?: number, effortThreshold?: number }} [options]
 * @param {Object} [filters]
 */
export async function getCorrelation(
  { effortAxis = "coverage_percent", salesThreshold, effortThreshold } = {},
  filters = {},
) {
  const { yearmonth, tenureBand } = filters || {};
  const params = { effort_axis: effortAxis, ...geoParams(filters) };
  setScalarParam(params, "yearmonth", yearmonth);
  setListParam(params, "tenure_band", tenureBand);
  setScalarParam(params, "sales_threshold", salesThreshold);
  setScalarParam(params, "effort_threshold", effortThreshold);
  const res = await envelopeGet(EP.CORRELATION, params);
  const q = res.quadrants || {};
  return {
    effortAxis: res.effortAxis ?? effortAxis,
    salesThreshold: res.salesThreshold ?? null,
    effortThreshold: res.effortThreshold ?? null,
    quadrants: {
      highEffortHighSales: q.highEffortHighSales ?? 0,
      highEffortLowSales: q.highEffortLowSales ?? 0,
      lowEffortHighSales: q.lowEffortHighSales ?? 0,
      lowEffortLowSales: q.lowEffortLowSales ?? 0,
    },
    rows: asArray(res.rows).map(normEmployeeTrendRow),
    employeeCount: res.employeeCount ?? 0,
  };
}

/**
 * Effort grouped by division/state/hq (latest month totals).
 * GET efforts-dashboard/by-group?group_by
 * @param {string} [groupBy='division']
 */
export async function getByGroup(groupBy = "division", filters = {}) {
  const res = await envelopeGet(EP.BY_GROUP, { group_by: groupBy, ...effortsParams(filters) });
  return {
    groupBy: res.groupBy || groupBy,
    months: asArray(res.months),
    groups: asArray(res.groups),
  };
}

/**
 * Field users inside one band of a KPI card.
 * GET efforts-dashboard/band-employees?metric&band
 * @param {string} metricId card id or wire metric
 * @param {number} band band index 0..3
 */
export async function getBandEmployees(metricId, band, filters = {}) {
  const metric = BAND_METRIC_PARAM[metricId] ?? metricId;
  const bandIndex = Number(band) || 0;
  const res = await envelopeGet(EP.BAND_EMPLOYEES, {
    metric,
    band: bandIndex,
    ...effortsParams(filters),
  });
  const employees = asArray(res.employees);
  return {
    metric: res.metric ?? metric,
    band: res.band ?? bandIndex,
    bandLabel: res.bandLabel ?? "",
    total: res.total ?? employees.length,
    employees,
  };
}

/**
 * Upload an xlsx/csv file and report progress.
 *
 * NOTE: kept so `useFileUpload` in hooks/useEffortsDashboard.js still resolves.
 * No page calls it today, and the backend path is unverified: the source class
 * posted to `/upload` through the middleware proxy. Confirm the real route
 * before wiring an upload UI.
 *
 * @param {File} file
 * @param {(pct: number) => void} [onProgress]
 */
export async function uploadFile(file, onProgress) {
  const form = new FormData();
  form.append("file", file);
  try {
    const response = await apiClient.post(getApiUrl(EP.UPLOAD), form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (event) => {
        if (onProgress && event.total) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      },
    });
    return unwrapEnvelope(response);
  } catch (error) {
    throw asEffortsApiError(error);
  }
}

/**
 * Status of an asynchronous upload job. Same caveat as `uploadFile`.
 * @param {string} jobId
 */
export async function getJobStatus(jobId) {
  return envelopeGet(EP.JOB_STATUS(encodeURIComponent(jobId)));
}
